import {
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
} from 'types'

export type DataValue = number

/**
 * The summary statistics for categorical data, where:
 *    value_range is the range from min-value to max, according to the numerical value
 *    num_values is the total number of valid (non-NaN) values
 *    percentages is a mapping for each valid value found in the dataset to the percentage of num_values it comprises
 */
export type CategoricalSummaryStatistics = {
  value_range: GeckoMeasurementRange
  num_values: number
  percentages: Record<DataValue, GeckoMeasurement>
}

/**
 * A version of the CategoricalSummaryStatistics that is serializable
 */
export type RawCategoricalSummaryStatistics = {
  value_range: {
    start: number
    end: number
    unit: string
  }
  percentages: Record<DataValue, number>
  num_values: number
}

/**
 * Serializes the CategoricalSummaryStatistics into a RawCategoricalSummaryStatistics so
 * that it can be passed to and from web-workers.
 */
export function serializeCategoricalSummaryStatistics(
  data: CategoricalSummaryStatistics
): RawCategoricalSummaryStatistics {
  const serializedPercentages = Object.fromEntries(
    Object.entries(data.percentages).map(([key, value]) => [key, value.num])
  )
  return {
    value_range: GeckoMeasurementRange.Serialize(data.value_range),
    percentages: serializedPercentages,
    num_values: data.num_values,
  }
}

/**
 * Deserializes the RawCategoricalSummaryStatistics into a CategoricalSummaryStatistics, useful
 * for getting the full data-structure after it has been passed to/from a web-worker.
 */
export function deserializeCategoricalSummaryStats(
  data: RawCategoricalSummaryStatistics
): CategoricalSummaryStatistics {
  const unSerializedPercentages = Object.fromEntries(
    Object.entries(data.percentages).map(([key, value]) => [
      key,
      GeckoMeasurement.CreateFromValue(value, GeckoMeasurementUnit.Percent),
    ])
  )
  return {
    value_range: GeckoMeasurementRange.CreateFromSerializable(data.value_range),
    percentages: unSerializedPercentages,
    num_values: data.num_values,
  }
}
